import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Pressable,
  Platform,
  Alert,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { ChargerAttributes } from '../../models/charger-model';
import { getAllChargers } from '../../store/actions/charging-actions';
import { isSuccessful } from '../../services/api-requests';
import {
  OcppErrorCode,
  OcppSimulatorStatus,
  ocppToggleCable,
  ocppSimulateFault,
  ocppResetCharger,
  ocppForceStop,
  ocppGetAllStatus,
  ocppGetHealth,
} from '../../services/charger-service';

// ────────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────────

interface ErrorOption {
  code: OcppErrorCode;
  label: string;
  description: string;
  icon: keyof typeof MaterialIcons.glyphMap;
  color: string;
}

const ERROR_OPTIONS: ErrorOption[] = [
  { code: 'GroundFailure', label: 'Ground Failure', description: 'Ground fault detected', icon: 'flash-on', color: '#EF4444' },
  { code: 'HighTemperature', label: 'High Temperature', description: 'Temperature too high', icon: 'whatshot', color: '#F97316' },
  { code: 'OverCurrentFailure', label: 'Over Current', description: 'Current exceeded limits', icon: 'bolt', color: '#EAB308' },
  { code: 'OverVoltage', label: 'Over Voltage', description: 'Voltage too high', icon: 'electric-bolt', color: '#F59E0B' },
  { code: 'PowerMeterFailure', label: 'Power Meter Failure', description: 'Power meter malfunction', icon: 'speed', color: '#A855F7' },
  { code: 'ConnectorLockFailure', label: 'Connector Lock', description: 'Failure to lock/unlock connector', icon: 'lock-open', color: '#EC4899' },
  { code: 'InternalError', label: 'Internal Error', description: 'Internal charger error', icon: 'error', color: '#DC2626' },
  { code: 'OtherError', label: 'Other Error', description: 'Generic error', icon: 'warning', color: '#6B7280' },
];

// ────────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────────

const OcppSimulator: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const chargers: ChargerAttributes[] = useSelector(
    (state: RootState) => state.chargers?.chargers || []
  );

  const [selectedCharger, setSelectedCharger] = useState<ChargerAttributes | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showChargerDropdown, setShowChargerDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [simulatorStatuses, setSimulatorStatuses] = useState<OcppSimulatorStatus[]>([]);
  const [healthStatus, setHealthStatus] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showErrorPicker, setShowErrorPicker] = useState(false);

  useEffect(() => {
    dispatch(getAllChargers());
  }, [dispatch]);

  const filteredChargers = chargers.filter((charger) => {
    const searchLower = searchQuery.toLowerCase();
    const name = charger.name?.toLowerCase() || '';
    const serial = charger.unit_serial?.toLowerCase() || '';
    return name.includes(searchLower) || serial.includes(searchLower);
  });

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      alert(`${title}: ${message}`);
    } else {
      Alert.alert(title, message);
    }
  };

  const handleChargerSelect = (charger: ChargerAttributes) => {
    setSelectedCharger(charger);
    setShowChargerDropdown(false);
    setSearchQuery('');
    setLastResult(null);
  };

  // Get the simulator status for the currently selected charger
  // Match on unit_serial, meander_serial, or manufacturer_device_id (case-insensitive)
  const selectedChargerStatus = simulatorStatuses.find((s) => {
    if (!selectedCharger || !s.charger_id) return false;
    const simId = s.charger_id.toLowerCase();
    return (
      simId === selectedCharger.unit_serial?.toLowerCase() ||
      simId === selectedCharger.meander_serial?.toLowerCase() ||
      simId === selectedCharger.manufacturer_device_id?.toLowerCase()
    );
  });

  // ── Actions ──────────────────────────────────────────────────────────────────

  const handleToggleCable = async () => {
    if (!selectedCharger) return;
    setActionLoading('cable');
    setLastResult(null);
    try {
      const response = await ocppToggleCable(selectedCharger.unit_serial);
      if (isSuccessful(response.status)) {
        const msg = (response.payload as any)?.message || 'Cable toggled successfully';
        setLastResult({ success: true, message: msg });
      } else {
        setLastResult({ success: false, message: 'Failed to toggle cable connection' });
      }
      // Refresh statuses
      await refreshStatuses();
    } catch (err) {
      setLastResult({ success: false, message: `Error: ${err}` });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReset = async () => {
    if (!selectedCharger) return;
    setActionLoading('reset');
    setLastResult(null);
    try {
      const response = await ocppResetCharger(selectedCharger.unit_serial);
      if (isSuccessful(response.status)) {
        const msg = (response.payload as any)?.message || 'Charger reset successfully';
        setLastResult({ success: true, message: msg });
      } else {
        setLastResult({ success: false, message: 'Failed to reset charger' });
      }
      await refreshStatuses();
    } catch (err) {
      setLastResult({ success: false, message: `Error: ${err}` });
    } finally {
      setActionLoading(null);
    }
  };

  const handleForceStop = async () => {
    if (!selectedCharger) return;
    setActionLoading('stop');
    setLastResult(null);
    try {
      const response = await ocppForceStop(selectedCharger.unit_serial);
      if (isSuccessful(response.status)) {
        const msg = (response.payload as any)?.message || 'Charging stopped';
        setLastResult({ success: true, message: msg });
      } else {
        setLastResult({ success: false, message: 'Failed to stop charging' });
      }
      await refreshStatuses();
    } catch (err) {
      setLastResult({ success: false, message: `Error: ${err}` });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSimulateFault = async (errorCode: OcppErrorCode) => {
    if (!selectedCharger) return;
    setActionLoading(`fault-${errorCode}`);
    setLastResult(null);
    setShowErrorPicker(false);
    try {
      const response = await ocppSimulateFault(selectedCharger.unit_serial, errorCode);
      if (isSuccessful(response.status)) {
        const msg = (response.payload as any)?.message || `Fault ${errorCode} simulated`;
        setLastResult({ success: true, message: msg });
      } else {
        setLastResult({ success: false, message: `Failed to simulate fault: ${errorCode}` });
      }
      await refreshStatuses();
    } catch (err) {
      setLastResult({ success: false, message: `Error: ${err}` });
    } finally {
      setActionLoading(null);
    }
  };

  const looksLikeChargerStatus = (obj: any): boolean => {
    if (typeof obj !== 'object' || obj === null) return false;
    // Must have at least one simulator / OCPP-related field
    return (
      'connector_status' in obj ||
      'vehicle_connected' in obj ||
      'vehicle_charging' in obj ||
      'cable_connected' in obj ||
      'state' in obj ||
      'status' in obj ||
      'connectorStatus' in obj
    );
  };

  const capitalizeState = (state: string | null | undefined): string => {
    if (!state) return 'Unknown';
    return state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
  };

  const normalizeChargerStatus = (raw: any, id?: string): OcppSimulatorStatus => ({
    // Map simulator fields to our normalized shape
    charger_id: raw.charger_id || raw.chargerId || raw.id || raw.serial || id || 'unknown',
    connector_status: capitalizeState(raw.connector_status || raw.connectorStatus || raw.state || raw.status),
    vehicle_connected: raw.vehicle_connected ?? raw.vehicleConnected ?? raw.cable_connected ?? false,
    vehicle_charging: raw.vehicle_charging ?? raw.vehicleCharging ?? (raw.state === 'charging') ?? false,
    error_code: raw.error_code || raw.errorCode || raw.fault_code || null,
    // Preserve all original fields for display
    ...raw,
  });

  const parseSimulatorStatuses = (payload: any): OcppSimulatorStatus[] => {
    console.log('[OcppSimulator] Raw status payload:', JSON.stringify(payload).slice(0, 1000));

    // Handle: direct array  [ { charger_id, ... }, ... ]
    if (Array.isArray(payload)) {
      return payload.filter(looksLikeChargerStatus).map((item) => normalizeChargerStatus(item));
    }

    // Unwrap common wrapper patterns: { data: ... }, { chargers: ... }, { results: ... }
    const innerData = payload?.data ?? payload?.chargers ?? payload?.results;

    // Wrapped array
    if (Array.isArray(innerData)) {
      return innerData.filter(looksLikeChargerStatus).map((item: any) => normalizeChargerStatus(item));
    }

    // Wrapped object keyed by charger ID:  { data: { "INTER-001": {...}, ... } }
    const objectToParse = (innerData && typeof innerData === 'object') ? innerData : payload;

    if (typeof objectToParse === 'object' && objectToParse !== null) {
      const keys = Object.keys(objectToParse);
      const entries = keys
        .filter((key) => looksLikeChargerStatus(objectToParse[key]))
        .map((key) => normalizeChargerStatus(objectToParse[key], key));
      if (entries.length > 0) {
        return entries;
      }
    }

    console.warn('[OcppSimulator] Could not parse status payload into charger entries. Keys:', 
      typeof payload === 'object' ? Object.keys(payload || {}) : typeof payload);
    return [];
  };

  const parseHealthStatus = (payload: any): string => {
    // Direct shape: { status: "ok" }
    if (typeof payload?.status === 'string') return payload.status;
    // Wrapped: { data: { status: "ok" } }
    if (typeof payload?.data?.status === 'string') return payload.data.status;
    // Plain string
    if (typeof payload === 'string') return payload;
    // Boolean success field: { success: true }
    if (typeof payload?.success === 'boolean') return payload.success ? 'ok' : 'error';
    // Wrapped string: { data: "ok" }
    if (typeof payload?.data === 'string') return payload.data;
    console.warn('[OcppSimulator] Could not parse health payload:', JSON.stringify(payload).slice(0, 300));
    return 'unknown';
  };

  const refreshStatuses = useCallback(async () => {
    try {
      const [statusRes, healthRes] = await Promise.all([
        ocppGetAllStatus(),
        ocppGetHealth(),
      ]);
      if (isSuccessful(statusRes.status) && statusRes.payload) {
        const parsed = parseSimulatorStatuses(statusRes.payload);
        setSimulatorStatuses(parsed);
      }
      if (isSuccessful(healthRes.status) && healthRes.payload) {
        const parsed = parseHealthStatus(healthRes.payload);
        setHealthStatus(parsed);
      }
    } catch (err) {
      console.error('Error refreshing OCPP statuses:', err);
    }
  }, []);

  const handleRefreshAll = async () => {
    setLoading(true);
    setLastResult(null);
    await refreshStatuses();
    setLoading(false);
  };

  useEffect(() => {
    refreshStatuses();
  }, [refreshStatuses]);

  // Treat various "healthy" strings as connected
  const isHealthy = !!healthStatus && ['ok', 'healthy', 'running', 'connected', 'up'].includes(healthStatus.toLowerCase());

  // ── Status helpers ─────────────────────────────────────────────────────────

  const getStatusColor = (status: string | undefined | null): string => {
    if (!status) return '#6B7280';
    const colors: Record<string, string> = {
      Available: '#51B868',
      Preparing: '#3B82F6',
      Charging: '#F97777',
      Faulted: '#EF4444',
      Finishing: '#8B5CF6',
      Reserved: '#F59E0B',
      Unavailable: '#6B7280',
      SuspendedEV: '#F59E0B',
      SuspendedEVSE: '#F59E0B',
    };
    return colors[status] || '#6B7280';
  };

  const getStatusIcon = (status: string | undefined | null): keyof typeof MaterialIcons.glyphMap => {
    if (!status) return 'help-outline';
    const icons: Record<string, keyof typeof MaterialIcons.glyphMap> = {
      Available: 'check-circle',
      Preparing: 'power',
      Charging: 'battery-charging-full',
      Faulted: 'error',
      Finishing: 'hourglass-bottom',
      Reserved: 'schedule',
      Unavailable: 'block',
    };
    return icons[status] || 'help-outline';
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
    >
      {/* Health Status Banner */}
      <View style={[
        styles.healthBanner,
        isHealthy ? styles.healthOk : styles.healthUnknown,
      ]}>
        <MaterialIcons
          name={isHealthy ? 'cloud-done' : 'cloud-off'}
          size={20}
          color={isHealthy ? '#51B868' : '#F59E0B'}
        />
        <Text style={styles.healthText}>
          OCPP Simulator: {isHealthy ? 'Connected' : healthStatus || 'Checking...'}
        </Text>
        <Pressable onPress={handleRefreshAll} style={styles.refreshIconButton}>
          {loading ? (
            <ActivityIndicator size="small" color="#00D9FF" />
          ) : (
            <MaterialIcons name="refresh" size={20} color="#00D9FF" />
          )}
        </Pressable>
      </View>

      {/* Charger Selection */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Select Charger</Text>
        <Text style={styles.sectionDescription}>
          Choose a registered charger to control its OCPP simulation
        </Text>

        <Pressable
          style={styles.chargerSelector}
          onPress={() => setShowChargerDropdown(!showChargerDropdown)}
        >
          <View style={styles.chargerSelectorContent}>
            {selectedCharger ? (
              <>
                <MaterialIcons name="ev-station" size={20} color="#95F5AA" />
                <Text style={styles.chargerSelectorText}>
                  {selectedCharger.name}: {selectedCharger.unit_serial}
                </Text>
              </>
            ) : (
              <>
                <MaterialIcons name="ev-station" size={20} color="#666" />
                <Text style={[styles.chargerSelectorText, { color: '#666' }]}>
                  Select a charger...
                </Text>
              </>
            )}
          </View>
          <MaterialIcons
            name={showChargerDropdown ? 'expand-less' : 'expand-more'}
            size={24}
            color="white"
          />
        </Pressable>

        {showChargerDropdown && (
          <View style={styles.dropdown}>
            <TextInput
              style={styles.searchInput}
              placeholder="Search chargers..."
              placeholderTextColor="#666"
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
            <ScrollView style={styles.dropdownList} nestedScrollEnabled>
              {filteredChargers.length > 0 ? (
                filteredChargers.map((charger) => (
                  <Pressable
                    key={charger.uuid}
                    style={({ pressed }) => [
                      styles.dropdownItem,
                      pressed && styles.dropdownItemPressed,
                    ]}
                    onPress={() => handleChargerSelect(charger)}
                  >
                    <MaterialIcons name="ev-station" size={18} color="#95F5AA" />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.dropdownItemText}>
                        {charger.name}
                      </Text>
                      <Text style={styles.dropdownItemSubtext}>
                        {charger.unit_serial}
                      </Text>
                    </View>
                  </Pressable>
                ))
              ) : (
                <Text style={styles.noResultsText}>No chargers found</Text>
              )}
            </ScrollView>
          </View>
        )}
      </View>

      {/* Current Status Card */}
      {selectedCharger && (
        <View style={styles.section}>
          <View style={styles.statusHeader}>
            <Text style={styles.sectionTitle}>Charger Status</Text>
            <Pressable onPress={handleRefreshAll} disabled={loading}>
              <MaterialIcons name="refresh" size={22} color="#00D9FF" />
            </Pressable>
          </View>

          {selectedChargerStatus ? (
            <View style={styles.statusGrid}>
              {/* Row 1: State + Cable */}
              <View style={styles.statusRow}>
                <View style={styles.statusItem}>
                  <Text style={styles.statusLabel}>State</Text>
                  <View style={styles.statusValueRow}>
                    <MaterialIcons
                      name={getStatusIcon(selectedChargerStatus.connector_status)}
                      size={18}
                      color={getStatusColor(selectedChargerStatus.connector_status)}
                    />
                    <Text style={[
                      styles.statusValue,
                      { color: getStatusColor(selectedChargerStatus.connector_status) },
                    ]}>
                      {selectedChargerStatus.connector_status || 'Unknown'}
                    </Text>
                  </View>
                </View>
                <View style={styles.statusItem}>
                  <Text style={styles.statusLabel}>Cable</Text>
                  <View style={styles.statusValueRow}>
                    <MaterialIcons
                      name={selectedChargerStatus.vehicle_connected ? 'power' : 'power-off'}
                      size={18}
                      color={selectedChargerStatus.vehicle_connected ? '#3B82F6' : '#6B7280'}
                    />
                    <Text style={[
                      styles.statusValue,
                      { color: selectedChargerStatus.vehicle_connected ? '#3B82F6' : '#6B7280' },
                    ]}>
                      {selectedChargerStatus.vehicle_connected ? 'Connected' : 'Disconnected'}
                    </Text>
                  </View>
                </View>
              </View>
              {/* Row 2: Charging + Error */}
              <View style={styles.statusRow}>
                <View style={styles.statusItem}>
                  <Text style={styles.statusLabel}>Charging</Text>
                  <View style={styles.statusValueRow}>
                    <MaterialIcons
                      name={selectedChargerStatus.vehicle_charging ? 'battery-charging-full' : 'battery-std'}
                      size={18}
                      color={selectedChargerStatus.vehicle_charging ? '#95F5AA' : '#6B7280'}
                    />
                    <Text style={[
                      styles.statusValue,
                      { color: selectedChargerStatus.vehicle_charging ? '#95F5AA' : '#6B7280' },
                    ]}>
                      {selectedChargerStatus.vehicle_charging ? 'Yes' : 'No'}
                    </Text>
                  </View>
                </View>
                <View style={styles.statusItem}>
                  <Text style={styles.statusLabel}>Error</Text>
                  <View style={styles.statusValueRow}>
                    <MaterialIcons
                      name={selectedChargerStatus.error_code ? 'error' : 'check'}
                      size={18}
                      color={selectedChargerStatus.error_code ? '#EF4444' : '#51B868'}
                    />
                    <Text style={[
                      styles.statusValue,
                      { color: selectedChargerStatus.error_code ? '#EF4444' : '#51B868' },
                    ]}>
                      {selectedChargerStatus.error_code || 'None'}
                    </Text>
                  </View>
                </View>
              </View>
              {/* Row 3: Power + Energy */}
              <View style={styles.statusRow}>
                <View style={styles.statusItem}>
                  <Text style={styles.statusLabel}>Power</Text>
                  <View style={styles.statusValueRow}>
                    <MaterialIcons name="flash-on" size={18} color="#F59E0B" />
                    <Text style={[styles.statusValue, { color: '#F59E0B' }]}>
                      {selectedChargerStatus.current_power != null
                        ? `${(selectedChargerStatus.current_power / 1000).toFixed(1)} kW`
                        : 'N/A'}
                    </Text>
                  </View>
                </View>
                <View style={styles.statusItem}>
                  <Text style={styles.statusLabel}>Energy</Text>
                  <View style={styles.statusValueRow}>
                    <MaterialIcons name="electrical-services" size={18} color="#00D9FF" />
                    <Text style={[styles.statusValue, { color: '#00D9FF' }]}>
                      {selectedChargerStatus.energy_wh != null
                        ? `${(selectedChargerStatus.energy_wh / 1000).toFixed(2)} kWh`
                        : 'N/A'}
                    </Text>
                  </View>
                </View>
              </View>
              {/* Row 4: Vendor + Transaction */}
              <View style={styles.statusRow}>
                <View style={styles.statusItem}>
                  <Text style={styles.statusLabel}>Device</Text>
                  <Text style={[styles.statusValue, { color: '#AAA', fontSize: 13 }]}>
                    {[selectedChargerStatus.vendor, selectedChargerStatus.model]
                      .filter(Boolean)
                      .join(' ') || 'Unknown'}
                  </Text>
                </View>
                <View style={styles.statusItem}>
                  <Text style={styles.statusLabel}>Transaction</Text>
                  <Text style={[styles.statusValue, { color: '#AAA', fontSize: 13 }]}>
                    {selectedChargerStatus.transaction_id || 'None'}
                  </Text>
                </View>
              </View>
            </View>
          ) : (
            <View style={styles.noStatusContainer}>
              <MaterialIcons name="info-outline" size={20} color="#666" />
              <Text style={styles.noStatusText}>
                No simulator status available for this charger. It may not be registered with the OCPP simulator.
              </Text>
            </View>
          )}
        </View>
      )}

      {/* Quick Actions */}
      {selectedCharger && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Quick Actions</Text>
          <Text style={styles.sectionDescription}>
            Control the simulated charger state
          </Text>

          <View style={styles.actionsGrid}>
            {/* Toggle Cable */}
            <Pressable
              style={({ pressed }) => [
                styles.actionCard,
                styles.actionCable,
                pressed && styles.actionCardPressed,
                actionLoading === 'cable' && styles.actionCardLoading,
              ]}
              onPress={handleToggleCable}
              disabled={!!actionLoading}
            >
              {actionLoading === 'cable' ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <MaterialIcons name="power" size={28} color="white" />
              )}
              <Text style={styles.actionCardTitle}>Toggle Cable</Text>
              <Text style={styles.actionCardDescription}>
                Connect / Disconnect vehicle
              </Text>
            </Pressable>

            {/* Reset */}
            <Pressable
              style={({ pressed }) => [
                styles.actionCard,
                styles.actionReset,
                pressed && styles.actionCardPressed,
                actionLoading === 'reset' && styles.actionCardLoading,
              ]}
              onPress={handleReset}
              disabled={!!actionLoading}
            >
              {actionLoading === 'reset' ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <MaterialIcons name="restart-alt" size={28} color="white" />
              )}
              <Text style={styles.actionCardTitle}>Reset Charger</Text>
              <Text style={styles.actionCardDescription}>
                Return to Available
              </Text>
            </Pressable>

            {/* Force Stop */}
            <Pressable
              style={({ pressed }) => [
                styles.actionCard,
                styles.actionStop,
                pressed && styles.actionCardPressed,
                actionLoading === 'stop' && styles.actionCardLoading,
              ]}
              onPress={handleForceStop}
              disabled={!!actionLoading}
            >
              {actionLoading === 'stop' ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <MaterialIcons name="stop-circle" size={28} color="white" />
              )}
              <Text style={styles.actionCardTitle}>Force Stop</Text>
              <Text style={styles.actionCardDescription}>
                Stop active charge
              </Text>
            </Pressable>

            {/* Simulate Error */}
            <Pressable
              style={({ pressed }) => [
                styles.actionCard,
                styles.actionFault,
                pressed && styles.actionCardPressed,
                showErrorPicker && styles.actionCardActive,
              ]}
              onPress={() => setShowErrorPicker(!showErrorPicker)}
              disabled={!!actionLoading}
            >
              <MaterialIcons name="warning" size={28} color="white" />
              <Text style={styles.actionCardTitle}>Simulate Fault</Text>
              <Text style={styles.actionCardDescription}>
                Trigger an error code
              </Text>
            </Pressable>
          </View>
        </View>
      )}

      {/* Error Code Picker */}
      {showErrorPicker && selectedCharger && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Select Error Code</Text>
          <Text style={styles.sectionDescription}>
            Choose a fault to simulate on {selectedCharger.name}
          </Text>

          <View style={styles.errorGrid}>
            {ERROR_OPTIONS.map((error) => {
              const isLoading = actionLoading === `fault-${error.code}`;
              return (
                <Pressable
                  key={error.code}
                  style={({ pressed }) => [
                    styles.errorCard,
                    { borderColor: error.color + '66' },
                    pressed && styles.errorCardPressed,
                    isLoading && styles.actionCardLoading,
                  ]}
                  onPress={() => handleSimulateFault(error.code)}
                  disabled={!!actionLoading}
                >
                  {isLoading ? (
                    <ActivityIndicator size="small" color={error.color} />
                  ) : (
                    <MaterialIcons name={error.icon} size={24} color={error.color} />
                  )}
                  <View style={styles.errorCardText}>
                    <Text style={[styles.errorCardTitle, { color: error.color }]}>
                      {error.label}
                    </Text>
                    <Text style={styles.errorCardDescription}>
                      {error.description}
                    </Text>
                  </View>
                </Pressable>
              );
            })}
          </View>
        </View>
      )}

      {/* Result Banner */}
      {lastResult && (
        <View style={[
          styles.resultBanner,
          lastResult.success ? styles.resultSuccess : styles.resultError,
        ]}>
          <MaterialIcons
            name={lastResult.success ? 'check-circle' : 'error'}
            size={20}
            color={lastResult.success ? '#51B868' : '#EF4444'}
          />
          <Text style={[
            styles.resultText,
            { color: lastResult.success ? '#51B868' : '#EF4444' },
          ]}>
            {lastResult.message}
          </Text>
          <Pressable onPress={() => setLastResult(null)}>
            <MaterialIcons name="close" size={18} color="#666" />
          </Pressable>
        </View>
      )}

      {/* All Simulator Statuses */}
      {simulatorStatuses.length > 0 && (
        <View style={styles.section}>
          <View style={styles.statusHeader}>
            <Text style={styles.sectionTitle}>All Simulator Chargers</Text>
            <Text style={styles.chargerCount}>{simulatorStatuses.length}</Text>
          </View>

          <View style={styles.allStatusList}>
            {simulatorStatuses.map((status, index) => {
              const simId = status.charger_id?.toLowerCase();
              const findMatch = (c: ChargerAttributes) => {
                return (
                  c.unit_serial?.toLowerCase() === simId ||
                  c.meander_serial?.toLowerCase() === simId ||
                  c.manufacturer_device_id?.toLowerCase() === simId
                );
              };
              const matchedCharger = chargers.find(findMatch);
              const isRegistered = !!matchedCharger;
              const isSelected = !!selectedCharger && findMatch(selectedCharger);
              return (
                <Pressable
                  key={status.charger_id || `sim-${index}`}
                  style={({ pressed }) => [
                    styles.allStatusCard,
                    pressed && styles.allStatusCardPressed,
                    isSelected && styles.allStatusCardSelected,
                  ]}
                  onPress={() => {
                    if (matchedCharger) {
                      setSelectedCharger(matchedCharger);
                      setLastResult(null);
                    } else {
                      showAlert(
                        'Not Registered',
                        `Charger "${status.charger_id}" is in the simulator but not registered to your account. Select it from the charger dropdown above to control it.`
                      );
                    }
                  }}
                >
                  <View style={styles.allStatusLeft}>
                    <View style={[
                      styles.statusDot,
                      { backgroundColor: getStatusColor(status.connector_status) },
                    ]} />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.allStatusId}>
                        {status.charger_id || 'Unknown ID'}
                      </Text>
                      <Text style={styles.allStatusDetail}>
                        {status.connector_status || 'No status'}
                        {status.vehicle_connected ? ' | Vehicle Connected' : ''}
                        {status.vehicle_charging ? ' | Charging' : ''}
                      </Text>
                    </View>
                  </View>
                  <View style={{ alignItems: 'flex-end', gap: 4 }}>
                    {status.error_code && (
                      <View style={styles.errorBadge}>
                        <MaterialIcons name="warning" size={14} color="#EF4444" />
                        <Text style={styles.errorBadgeText}>{status.error_code}</Text>
                      </View>
                    )}
                    {!isRegistered && (
                      <Text style={styles.unregisteredText}>Not registered</Text>
                    )}
                  </View>
                </Pressable>
              );
            })}
          </View>
        </View>
      )}

      {/* Info Section */}
      <View style={styles.infoSection}>
        <MaterialIcons name="info-outline" size={20} color="#00D9FF" />
        <Text style={styles.infoText}>
          OCPP Simulator controls are only available in Stage and Dev builds.
          These controls interact with the simulated OCPP chargers connected to
          the backend OCPP Central System.
        </Text>
      </View>
    </ScrollView>
  );
};

// ────────────────────────────────────────────────────────────────────────────────
// Styles
// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1E1E1E',
  },
  contentContainer: {
    padding: 16,
    gap: 16,
    paddingBottom: 40,
  },

  // Health Banner
  healthBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 10,
    gap: 10,
  },
  healthOk: {
    backgroundColor: 'rgba(81, 184, 104, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(81, 184, 104, 0.3)',
  },
  healthUnknown: {
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(245, 158, 11, 0.3)',
  },
  healthText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: 'white',
  },
  refreshIconButton: {
    padding: 4,
  },

  // Sections
  section: {
    backgroundColor: '#2A2A2A',
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: '#00D9FF33',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: 'white',
    marginBottom: 6,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#AAA',
    lineHeight: 20,
    marginBottom: 4,
  },

  // Charger Selector
  chargerSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#333',
    padding: 16,
    borderRadius: 8,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#444',
  },
  chargerSelectorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  chargerSelectorText: {
    fontSize: 16,
    color: 'white',
    flex: 1,
  },
  dropdown: {
    marginTop: 8,
    backgroundColor: '#333',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#444',
    maxHeight: 300,
  },
  searchInput: {
    backgroundColor: '#2A2A2A',
    color: 'white',
    padding: 12,
    borderRadius: 8,
    margin: 8,
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#444',
  },
  dropdownList: {
    maxHeight: 250,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#2A2A2A',
  },
  dropdownItemPressed: {
    backgroundColor: '#2A2A2A',
  },
  dropdownItemText: {
    fontSize: 15,
    color: 'white',
  },
  dropdownItemSubtext: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
  noResultsText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    padding: 20,
  },

  // Status Card
  statusHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  statusGrid: {
    gap: 12,
  },
  statusRow: {
    flexDirection: 'row',
    gap: 12,
  },
  statusItem: {
    flex: 1,
    backgroundColor: '#1E1E1E',
    borderRadius: 8,
    padding: 14,
    borderWidth: 1,
    borderColor: '#333',
  },
  statusLabel: {
    fontSize: 12,
    color: '#888',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  statusValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  statusValue: {
    fontSize: 15,
    fontWeight: '600',
  },
  noStatusContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    paddingVertical: 8,
  },
  noStatusText: {
    flex: 1,
    fontSize: 14,
    color: '#888',
    lineHeight: 20,
  },

  // Actions Grid
  actionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 16,
  },
  actionCard: {
    width: '47%',
    flexGrow: 1,
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    gap: 8,
    minHeight: 120,
    justifyContent: 'center',
  },
  actionCardPressed: {
    opacity: 0.85,
    transform: [{ scale: 0.97 }],
  },
  actionCardLoading: {
    opacity: 0.6,
  },
  actionCardActive: {
    borderWidth: 2,
    borderColor: '#F59E0B',
  },
  actionCable: {
    backgroundColor: '#1E40AF',
  },
  actionReset: {
    backgroundColor: '#166534',
  },
  actionStop: {
    backgroundColor: '#991B1B',
  },
  actionFault: {
    backgroundColor: '#92400E',
  },
  actionCardTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: 'white',
    textAlign: 'center',
  },
  actionCardDescription: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.7)',
    textAlign: 'center',
  },

  // Error Code Picker
  errorGrid: {
    gap: 10,
    marginTop: 12,
  },
  errorCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1E1E1E',
    borderRadius: 10,
    padding: 14,
    gap: 14,
    borderWidth: 1,
  },
  errorCardPressed: {
    backgroundColor: '#333',
    transform: [{ scale: 0.98 }],
  },
  errorCardText: {
    flex: 1,
  },
  errorCardTitle: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 2,
  },
  errorCardDescription: {
    fontSize: 13,
    color: '#888',
  },

  // Result Banner
  resultBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 10,
    gap: 10,
  },
  resultSuccess: {
    backgroundColor: 'rgba(81, 184, 104, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(81, 184, 104, 0.3)',
  },
  resultError: {
    backgroundColor: 'rgba(239, 68, 68, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.3)',
  },
  resultText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
  },

  // All Status List
  chargerCount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#00D9FF',
    backgroundColor: '#00D9FF22',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  allStatusList: {
    gap: 8,
  },
  allStatusCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#1E1E1E',
    borderRadius: 8,
    padding: 14,
    borderWidth: 1,
    borderColor: '#333',
  },
  allStatusCardPressed: {
    backgroundColor: '#333',
  },
  allStatusCardSelected: {
    borderColor: '#00D9FF',
    backgroundColor: '#00D9FF11',
  },
  allStatusLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  allStatusId: {
    fontSize: 15,
    fontWeight: '600',
    color: 'white',
  },
  allStatusDetail: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
  errorBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  errorBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#EF4444',
  },
  unregisteredText: {
    fontSize: 10,
    color: '#F59E0B',
    fontWeight: '500',
  },

  // Info Section
  infoSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#2A2A2A',
    borderRadius: 12,
    padding: 16,
    gap: 12,
    borderWidth: 1,
    borderColor: '#00D9FF33',
  },
  infoText: {
    flex: 1,
    fontSize: 13,
    color: '#AAA',
    lineHeight: 18,
  },
});

export default OcppSimulator;
